import React, { useEffect, useMemo, useState, useRef } from "react";
import "../styles/WaterRefill.css";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { createBooking, resetState } from "../features/booking/bookingSlice";
import { toast } from "react-toastify";

// ✅ FIXED PRICE SIZES
const sizes = [
  { litres: 5, price: 100 },
  { litres: 10, price: 150 },
  { litres: 20, price: 250 },
];

const schema = yup.object().shape({
  name: yup.string().required("Full name is required"),
  phone: yup.string().required("Phone number is required"),
  litres: yup.number().required("Select litres"),
  numberOfContainers: yup
    .number()
    .typeError("Enter number of containers")
    .min(1, "At least 1 container required")
    .required("Containers required"),
  apartment: yup.string().required("Apartment / Estate is required"),
  houseNumber: yup.string().required("House / Door number is required"),
  locationNotes: yup.string().required("Location notes are required"),
  preferredDate: yup.string().required("Delivery date is required"),
  preferredTime: yup.string().required("Delivery time is required"),
});

const WaterRefill = () => {
  const dispatch = useDispatch();

  const { isLoading, isSuccess, isError, message } = useSelector(
    (state) => state.booking
  );

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    litres: 5,
    numberOfContainers: 1,
    apartment: "",
    houseNumber: "",
    locationNotes: "",
    preferredDate: "",
    preferredTime: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});

  // ✅ PRICE CALCULATION (UPDATED)
  const totalPrice = useMemo(() => {
    const selected = sizes.find(
      (s) => s.litres === Number(formData.litres)
    );

    if (!selected) return 0;

    return selected.price * Number(formData.numberOfContainers || 0);
  }, [formData.litres, formData.numberOfContainers]);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Water refill booked successfully!");

      setFormData({
        name: "",
        phone: "",
        email: "",
        litres: 5,
        numberOfContainers: 1,
        apartment: "",
        houseNumber: "",
        locationNotes: "",
        preferredDate: "",
        preferredTime: "",
        notes: "",
      });

      setErrors({});
      dispatch(resetState());
    }

    if (isError) {
      toast.error(message || "Something went wrong");
      dispatch(resetState());
    }
  }, [isSuccess, isError, message, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await schema.validate(formData, { abortEarly: false });

      const bookingData = {
        serviceType: "water-refill",
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        litres: Number(formData.litres),
        numberOfContainers: Number(formData.numberOfContainers),
        apartment: formData.apartment,
        houseNumber: formData.houseNumber,
        locationNotes: formData.locationNotes,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        notes: formData.notes,
        price: totalPrice,
      };

      await dispatch(createBooking(bookingData)).unwrap();
    } catch (error) {
      if (error.inner) {
        const validationErrors = {};

        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });

        setErrors(validationErrors);
        toast.error("Please fill in all required fields.");
      } else {
        toast.error(error || "Something went wrong");
      }
    }
  };

  // ✅ SCROLL REVEAL
  const servicesRef = useRef(null);

  useEffect(() => {
    const revealElements =
      servicesRef.current?.querySelectorAll("[data-reveal]") || [];

    const scrollReveal = () => {
      revealElements.forEach((el) => {
        const elementIsInScreen =
          el.getBoundingClientRect().top < window.innerHeight / 1.15;

        if (elementIsInScreen) {
          el.classList.add("revealed");
        } else {
          el.classList.remove("revealed");
        }
      });
    };

    window.addEventListener("scroll", scrollReveal);
    scrollReveal();

    return () => {
      window.removeEventListener("scroll", scrollReveal);
    };
  }, []);

  return (
    <main className="water-page" ref={servicesRef}>
      <section className="water-hero">
        <span data-reveal>Clean & Safe Water</span>
        <h1 data-reveal>Water Refill Service</h1>
        <p data-reveal>Affordable and reliable water delivered to your doorstep.</p>
      </section>

           <section className="water-pricing">
        <h2 data-reveal>Water Price List</h2>

        <div className="water-table-wrapper" data-reveal>
          <table className="water-table">
            <thead>
              <tr>
                <th>Container Size</th>
                <th>Price per Container</th>
              </tr>
            </thead>

            <tbody>
              {sizes.map((item, i) => (
                <tr key={i}>
                  <td>{item.litres} Litres</td>
                  <td>KSh {item.price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
         </section>
      <section className="water-form-section">
        <div className="form-info">
          <span data-reveal>Order form</span>
          <h2 data-reveal>Book Water Refill</h2>
          <p data-reveal>Fill in your details and we’ll confirm your delivery schedule.</p>
        </div>

        <form className="water-form" onSubmit={handleSubmit} data-reveal>
          <div className="form-row">
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                name="name"
                placeholder="Your name"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && <small className="error-text">{errors.name}</small>}
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                name="phone"
                placeholder="+254..."
                value={formData.phone}
                onChange={handleChange}
              />
              {errors.phone && <small className="error-text">{errors.phone}</small>}
            </div>
          </div>

          <div className="form-group">
            <label>Email Address Optional</label>
            <input
              type="email"
              name="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

         <div className="form-row">
            <div className="form-group">
              <label>Select Container Size</label>
              <select
                name="litres"
                value={formData.litres}
                onChange={handleChange}
              >
                {sizes.map((s) => (
                  <option key={s.litres} value={s.litres}>
                    {s.litres} Litres - KSh {s.price}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Number of Containers</label>
              <input
                type="number"
                min="1"
                name="numberOfContainers"
                value={formData.numberOfContainers}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-total">
            Estimated Total: <strong>KSh {totalPrice.toLocaleString()}</strong>
          </div>

          <div className="form-group">
            <label>Apartment / Estate</label>
            <input
              type="text"
              name="apartment"
              placeholder="E.g., Runda Evergreen"
              value={formData.apartment}
              onChange={handleChange}
            />
            {errors.apartment && (
              <small className="error-text">{errors.apartment}</small>
            )}
          </div>

          <div className="form-group">
            <label>House / Door Number</label>
            <input
              type="text"
              name="houseNumber"
              placeholder="E.g., House 7"
              value={formData.houseNumber}
              onChange={handleChange}
            />
            {errors.houseNumber && (
              <small className="error-text">{errors.houseNumber}</small>
            )}
          </div>

          <div className="form-group">
            <label>Location Notes</label>
            <input
              type="text"
              name="locationNotes"
              placeholder="E.g., opposite Naivas"
              value={formData.locationNotes}
              onChange={handleChange}
            />
            {errors.locationNotes && (
              <small className="error-text">{errors.locationNotes}</small>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Preferred Delivery Date</label>
              <input
                type="date"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
              />
              {errors.preferredDate && (
                <small className="error-text">{errors.preferredDate}</small>
              )}
            </div>

            <div className="form-group">
              <label>Preferred Delivery Time</label>
              <input
                type="time"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
              />
              {errors.preferredTime && (
                <small className="error-text">{errors.preferredTime}</small>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Additional Notes</label>
            <textarea
              name="notes"
              rows="5"
              placeholder="Any delivery instructions?"
              value={formData.notes}
              onChange={handleChange}
            ></textarea>
          </div>

          <button type="submit" className="submit-btn" disabled={isLoading}>
            {isLoading ? "Submitting..." : "Place Order"}
          </button>
        </form>
      </section>
    </main>
  );
};

export default WaterRefill;

 
